import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api';
import { useSessao } from '../sessao';
import { plural } from '../formato';
import { Aviso, Carregando, ErroDaApi, Selo, SeloDoEnsaio, Vazio } from '../ui';
import { NomeDaAmostra } from '../NomeDaAmostra';
import type { Os } from './OsForms';
import { AcoesDaOs, OsPeloNumero, UUID } from './OsFicha';
import { AbasDaOs, CabecalhoDaOs, proximoPasso, ReguaDaOs, rotaDaAba } from './OsMoldura';

/**
 * A aba «Cronograma» da OS — onda 304 (RN-249).
 *
 * «O que acha de incluir o cronograma da OS como uma aba depois de
 *  Programação. Ali seria apenas para visualizar o cronograma e o andamento
 *  da execução dos ensaios, eventuais atrasos, etc.»
 *                                                — Humberto, 08/10/2026
 *
 * Aprovado no quadro (Carbon Testesolo). O que a aba NÃO faz é tão
 * importante quanto o que faz:
 *
 *  · não muda data, máquina nem ordem — isso é o cronograma geral (onda
 *    266, `/cronograma`), onde a reprogramação guarda o motivo e a data
 *    original. Duas telas que reprogramam são duas trilhas que discordam;
 *  · não inclui nem tira ensaio — isso é a aba Programação.
 *
 * A conta do atraso é do servidor (`GET /os/:id/cronograma`, a regra em
 * `api/src/os/o-cronograma-da-os.ts`): a tela só desenha. Tudo em dias
 * corridos (decisão do Humberto, 02/10/2026).
 *
 * A informação nunca vai só na cor (§18): cada linha diz em TEXTO a
 * situação, as datas e o atraso; a régua e as barras são decorativas
 * (`aria-hidden`), um retrato do que o texto já disse.
 */

/* ── O que a API entrega ──────────────────────────────────────────── */

export interface LinhaDoCronograma {
  tipo: 'ensaio' | 'prevista';
  id: string;
  ensaio_id: string | null;
  prevista_id: string | null;
  amostra_id: string | null;
  amostra_codigo: string | null;
  amostra_identificacao: string | null;
  metodo_codigo: string;
  metodo_nome: string;
  escopo: string | null;
  status_ensaio: string | null;
  situacao: string;
  concluido: boolean;
  em_execucao: boolean;
  aguardando_amostra: boolean;
  na_agenda: boolean;
  equipamento_codigo: string | null;
  frente_nome: string | null;
  inicio_previsto: string | null;
  fim_previsto: string | null;
  inicio_real: string | null;
  fim_real: string | null;
  previsao_chegada: string | null;
  tipo_atraso: 'laboratorio' | 'amostra' | 'concluido_com_atraso' | null;
  atraso_dias: number;
}

export interface CronogramaDaOs {
  hoje: string;
  prazo_contratual: string | null;
  resumo: {
    total: number;
    concluidos: number;
    em_execucao: number;
    programados_a_seguir: number;
    atrasados_laboratorio: number;
    atrasados_amostra: number;
    sem_data: number;
    ultimo_fim_previsto: string | null;
    folga_dias: number | null;
    hoje: string;
  };
  linhas: LinhaDoCronograma[];
}

/* ── As datas, em dias corridos ───────────────────────────────────── */

/** `AAAA-MM-DD` → número do dia (UTC, sem fuso para escorregar). */
const emDias = (iso: string) => Math.round(Date.parse(`${iso}T00:00:00Z`) / 86400000);
const deDias = (n: number) => new Date(n * 86400000).toISOString().slice(0, 10);
/** «07/10» — o dia e o mês, como o quadro desenhou. */
const dm = (iso: string | null | undefined) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '—');
/** «07/10/2026» — a data inteira, para o prazo. */
const dma = (iso: string | null | undefined) =>
  (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '—');

/** Linha que tem lugar no Gantt: alguma data prevista (ou a chegada prevista). */
const temData = (l: LinhaDoCronograma) => !!(l.inicio_previsto || l.previsao_chegada);

/* ── O texto da linha (o que a barra desenha, dito por extenso) ────── */

/** O atraso por extenso — «! 2 d de atraso · não iniciado» (do quadro). */
export function textoDoAtraso(l: LinhaDoCronograma): string | null {
  const n = l.atraso_dias;
  switch (l.tipo_atraso) {
    case 'laboratorio':
      return l.inicio_real
        ? `! ${n} d de atraso · fim previsto ${dm(l.fim_previsto)}`
        : `! ${n} d de atraso · não iniciado`;
    case 'amostra':
      return l.previsao_chegada
        ? `! ${n} d · amostra não chegou (prevista ${dm(l.previsao_chegada)})`
        : `! ${n} d · amostra não chegou (início previsto ${dm(l.inicio_previsto)})`;
    case 'concluido_com_atraso':
      return `concluído ${n} d depois do previsto`;
    default:
      return null;
  }
}

/** As datas da linha — «03/10 → 07/10 · real 03/10 → hoje». */
function textoDasDatas(l: LinhaDoCronograma, hoje: string): string {
  if (l.tipo === 'prevista' || (!l.inicio_previsto && l.previsao_chegada)) {
    return l.previsao_chegada ? `chegada prevista ${dm(l.previsao_chegada)}` : 'sem previsão de chegada';
  }
  const partes = [`${dm(l.inicio_previsto)} → ${dm(l.fim_previsto)}`];
  if (l.inicio_real) {
    const fim = l.fim_real ?? (l.concluido ? null : hoje);
    partes.push(`real ${dm(l.inicio_real)} → ${fim === hoje && !l.fim_real ? 'hoje' : dm(fim)}`);
  }
  if (!l.na_agenda && l.tipo === 'ensaio') partes.push('sem máquina na agenda');
  return partes.join(' · ');
}

/** O selo da situação — os mesmos do sistema, mais o «Aguardando amostra». */
function SeloDaLinha({ l }: { l: LinhaDoCronograma }) {
  if (l.situacao === 'aguardando_amostra') {
    return (
      <Selo tom="atencao" titulo="A amostra ainda não passou pelo recebimento">
        Aguardando amostra
      </Selo>
    );
  }
  return <SeloDoEnsaio status={l.status_ensaio} />;
}

/* ── Os recortes ──────────────────────────────────────────────────── */

type Recorte = 'todos' | 'atrasados' | 'aberto' | 'concluidos';
const NOME_DO_RECORTE: Record<Recorte, string> = {
  todos: 'Todos', atrasados: 'Atrasados', aberto: 'Em aberto', concluidos: 'Concluídos',
};
const FILTRO: Record<Recorte, (l: LinhaDoCronograma) => boolean> = {
  todos: () => true,
  atrasados: (l) => l.tipo_atraso === 'laboratorio' || l.tipo_atraso === 'amostra',
  aberto: (l) => !l.concluido,
  concluidos: (l) => l.concluido,
};

/* ── A tela ───────────────────────────────────────────────────────── */

export function OsCronograma() {
  const { id = '' } = useParams();
  if (!id) return <Vazio>Abra o cronograma pela ficha da ordem de serviço.</Vazio>;
  /* O número da OS no endereço (favorito, link antigo) vira o id, na aba. */
  if (!UUID.test(id)) return <OsPeloNumero numero={id} aba="cronograma" />;
  return <TelaCronograma osId={id} key={id} />;
}

function TelaCronograma({ osId }: { osId: string }) {
  const { pode } = useSessao();
  const [os, setOs] = useState<Os | null>(null);
  const [crono, setCrono] = useState<CronogramaDaOs | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [erroCrono, setErroCrono] = useState<unknown>(null);

  const carregar = useCallback(async () => {
    try { setOs(await api<Os>(`/os/${osId}`)); } catch (e) { setErro(e); }
    try { setCrono(await api<CronogramaDaOs>(`/os/${osId}/cronograma`)); } catch (e) { setErroCrono(e); }
  }, [osId]);
  useEffect(() => { void carregar(); }, [carregar]);

  if (erro && !os) return <ErroDaApi erro={erro} />;
  if (!os) return <Carregando texto="Abrindo o cronograma da OS" />;

  const passo = proximoPasso(os, pode);
  const veEnsaios = pode('ensaio.ver');

  return (
    <div className="ficha-os cronograma-os">
      <CabecalhoDaOs os={os} passo={passo} acoes={<AcoesDaOs os={os} />} />
      <ReguaDaOs os={os} aba="cronograma" semMedicao={!pode('medicao.ver')}
        semAmostras={!pode('amostra.ver')} semProgramacao={!veEnsaios} />
      <AbasDaOs os={os} aba="cronograma" semQuantitativos={!pode('medicao.ver')}
        semAmostras={!pode('amostra.ver')} semProgramacao={!veEnsaios} />
      {erroCrono ? <ErroDaApi erro={erroCrono} />
        : !crono ? <Carregando texto="Montando o cronograma" />
          : <CorpoDoCronograma os={os} crono={crono} veEnsaios={veEnsaios} />}
    </div>
  );
}

function CorpoDoCronograma({ os, crono, veEnsaios }: {
  os: Os; crono: CronogramaDaOs; veEnsaios: boolean;
}) {
  const [recorte, setRecorte] = useState<Recorte>('todos');
  const { hoje, prazo_contratual: prazo, linhas } = crono;

  const visiveis = linhas.filter(FILTRO[recorte]);
  const noGantt = visiveis.filter(temData);
  const semData = visiveis.filter((l) => !temData(l));

  /* O primeiro dia dos ensaios: o cronograma geral abre a partir dele. */
  const primeiroDia = useMemo(() => {
    const ds = linhas.flatMap((l) => [l.inicio_previsto, l.inicio_real])
      .filter((x): x is string => !!x).sort();
    return ds[0] ?? null;
  }, [linhas]);
  const geral = `/cronograma?os=${os.id}`
    + (primeiroDia && primeiroDia < hoje ? `&de=${primeiroDia}` : '');

  return (
    <section className="cr-os" aria-labelledby="t-cronograma-os">
      <div className="cr-cabeca">
        <div>
          <h2 id="t-cronograma-os">Cronograma da OS</h2>
          <p className="sub">
            Previsto × realizado de cada ensaio desta OS, com os atrasos.
            {' '}Hoje é <span className="mono">{dma(hoje)}</span>.
          </p>
        </div>
      </div>

      <Aviso tipo="info">
        <p className="cr-aviso-texto">
          <b>Só visualização.</b> Para mudar datas, máquina ou ordem, abra no
          cronograma geral — lá a reprogramação guarda o motivo e a data
          original. Para incluir ou tirar ensaio, use a aba Programação.
        </p>
        {veEnsaios && (
          <p className="cr-aviso-acoes">
            <Link className="botao secundario" to={rotaDaAba(os.id, 'programacao')}>
              Programar ensaios
            </Link>
            <Link className="botao discreto" to={geral}>Abrir no cronograma geral →</Link>
          </p>
        )}
      </Aviso>

      <CartoesDoCronograma crono={crono} />

      <div className="ae-barra cr-ferramentas">
        <div className="ae-recortes" role="group" aria-label="Recorte dos ensaios">
          {(Object.keys(FILTRO) as Recorte[]).map((k) => (
            <button key={k} type="button" aria-pressed={recorte === k}
              className={recorte === k ? 'sel' : undefined}
              onClick={() => setRecorte(k)}>
              {NOME_DO_RECORTE[k]}{' '}
              <span className="aba-os-cont">{linhas.filter(FILTRO[k]).length}</span>
            </button>
          ))}
        </div>
        <ul className="cr-legenda" aria-label="Legenda do cronograma">
          <li><i className="cr-leg previsto" aria-hidden="true" />previsto</li>
          <li><i className="cr-leg realizado" aria-hidden="true" />realizado</li>
          <li><i className="cr-leg atraso" aria-hidden="true" />atraso</li>
          <li><i className="cr-leg hoje" aria-hidden="true" />hoje</li>
          <li><i className="cr-leg prazo" aria-hidden="true" />prazo contratual</li>
        </ul>
      </div>

      {linhas.length === 0 ? (
        <Vazio>
          Esta OS ainda não tem ensaio programado. Os ensaios aparecem aqui
          quando forem marcados na aba Programação.
        </Vazio>
      ) : visiveis.length === 0 ? (
        <p className="sub ae-vazio">Nenhum ensaio neste recorte.</p>
      ) : (
        <>
          {noGantt.length > 0 && (
            <Gantt linhas={noGantt} todas={linhas} hoje={hoje} prazo={prazo} />
          )}
          {semData.length > 0 && (
            <div className="cr-sem-data">
              <h3>Sem data no cronograma</h3>
              <p className="sub">
                Ensaios ainda sem data prevista — a data se dá no cronograma
                geral (ou, para a amostra por chegar, na previsão de chegada).
              </p>
              <ul>
                {semData.map((l) => (
                  <li key={l.id} data-linha={l.id}>
                    <span className="cr-metodo mono">{l.metodo_codigo}</span>
                    <AmostraDaLinha l={l} />
                    <SeloDaLinha l={l} />
                    {l.tipo === 'prevista' && <span className="det">sem previsão de chegada</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/** A amostra pelo nome do cliente (303); a por chegar, pela identificação prevista. */
function AmostraDaLinha({ l }: { l: LinhaDoCronograma }) {
  if (l.tipo === 'prevista') {
    return (
      <NomeDaAmostra emLinha amostra={{ codigo: null, identificacao: l.amostra_identificacao }}
        depois={<span className="det cr-por-chegar">por chegar</span>} />
    );
  }
  return (
    <NomeDaAmostra emLinha
      amostra={{ codigo: l.amostra_codigo, identificacao: l.amostra_identificacao }} />
  );
}

/* ── Os quatro cartões ────────────────────────────────────────────── */

function CartoesDoCronograma({ crono }: { crono: CronogramaDaOs }) {
  const { resumo: r, prazo_contratual: prazo } = crono;
  const pct = r.total > 0 ? Math.round((100 * r.concluidos) / r.total) : 0;
  const folga = r.folga_dias;
  return (
    <div className="cr-cartoes" role="group" aria-label="Resumo do cronograma">
      <div className="cr-cartao" data-cartao="andamento">
        <small>Andamento</small>
        <strong>{r.concluidos} de {r.total} concluídos</strong>
        <span className="cr-progresso" aria-hidden="true"><i style={{ width: `${pct}%` }} /></span>
      </div>
      <div className="cr-cartao" data-cartao="execucao">
        <small>Em execução</small>
        <strong>{r.em_execucao}</strong>
        <span className="det">{plural(r.programados_a_seguir, 'programado a seguir', 'programados a seguir')}</span>
      </div>
      <div className={`cr-cartao${r.atrasados_laboratorio > 0 ? ' critico' : ''}`} data-cartao="atrasados">
        <small>Atrasados</small>
        <strong>{r.atrasados_laboratorio > 0 ? `! ${r.atrasados_laboratorio}` : '0'}</strong>
        <span className="det">
          {r.atrasados_amostra > 0
            ? `+ ${r.atrasados_amostra} aguardando amostra do cliente`
            : 'do laboratório'}
        </span>
      </div>
      <div className={`cr-cartao${folga !== null && folga < 0 ? ' critico' : ''}`} data-cartao="prazo">
        <small>Prazo contratual</small>
        {prazo ? (
          <>
            <strong className="mono">{dma(prazo)}</strong>
            <span className={`det${folga !== null && folga < 0 ? ' cr-alem' : ''}`}>
              {r.ultimo_fim_previsto === null
                ? 'nenhum ensaio com data ainda'
                : folga !== null && folga < 0
                  ? `! ${plural(-folga, 'dia', 'dias')} além do prazo (último ensaio previsto para ${dm(r.ultimo_fim_previsto)})`
                  : `último ensaio previsto para ${dm(r.ultimo_fim_previsto)} · folga de ${plural(folga ?? 0, 'dia', 'dias')}`}
            </span>
          </>
        ) : (
          <>
            <strong>—</strong>
            <span className="det">sem prazo contratual na OS</span>
          </>
        )}
      </div>
    </div>
  );
}

/* ── O Gantt ──────────────────────────────────────────────────────── */

/** Largura mínima de um dia na régua, em px — abaixo disso a tela rola de lado. */
const DIA_MIN_PX = 14;

function Gantt({ linhas, todas, hoje, prazo }: {
  linhas: LinhaDoCronograma[]; todas: LinhaDoCronograma[];
  hoje: string; prazo: string | null;
}) {
  /*
   * A janela cobre do menor início ao maior entre fim, prazo e hoje, com
   * folga dos dois lados. Calculada sobre TODAS as linhas (não só as do
   * recorte): trocar de recorte não muda a régua — o olho não perde o lugar.
   */
  const { ini, dias } = useMemo(() => {
    const ds = [hoje, ...(prazo ? [prazo] : []),
      ...todas.flatMap((l) => [l.inicio_previsto, l.fim_previsto, l.inicio_real,
        l.fim_real, l.previsao_chegada])]
      .filter((x): x is string => !!x).map(emDias);
    let a = Math.min(...ds) - 2;
    let b = Math.max(...ds) + 3;
    if (b - a < 21) { const falta = 21 - (b - a); a -= Math.floor(falta / 2); b += Math.ceil(falta / 2); }
    /* Um prazo de dois anos não pode virar uma régua de 700 dias. */
    if (b - a > 240) b = a + 240;
    return { ini: a, dias: b - a };
  }, [todas, hoje, prazo]);

  const pos = (iso: string) => ((emDias(iso) - ini) / dias) * 100;
  /** Um trecho de dias inteiros: de `a` até o FIM do dia `b`. */
  const trecho = (a: string, b: string) => {
    const x = Math.max(0, pos(a));
    const fim = Math.min(100, ((emDias(b) + 1 - ini) / dias) * 100);
    return { left: `${x}%`, width: `${Math.max(0, fim - x)}%` };
  };
  const linhaDoDia = (iso: string) => ({ left: `${pos(iso) + 50 / dias}%` });

  const diasDaRegua = Array.from({ length: dias }, (_, i) => {
    const iso = deDias(ini + i);
    const sem = new Date(`${iso}T00:00:00Z`).getUTCDay();
    return { iso, dia: iso.slice(8, 10), fds: sem === 0 || sem === 6, segunda: sem === 1 };
  });
  const naJanela = (iso: string | null) => !!iso && emDias(iso) >= ini && emDias(iso) < ini + dias;

  return (
    <div className="cr-rola">
      <div className="cr-gantt" style={{ minWidth: `calc(var(--cr-esq) + ${dias * DIA_MIN_PX}px)` }}>
        <div className="cr-regua-linha">
          <div className="cr-esq cr-esq-cab">Ensaio · amostra</div>
          <div className="cr-trilho cr-regua" aria-hidden="true">
            {diasDaRegua.map((d, i) => (
              <span key={d.iso}
                className={`cr-dia${d.fds ? ' fds' : ''}${d.segunda ? ' semana' : ''}${d.iso === hoje ? ' hoje' : ''}`}
                style={{ left: `${(i / dias) * 100}%`, width: `${100 / dias}%` }}>
                {d.segunda && <em>{dm(d.iso)}</em>}
                {d.dia}
              </span>
            ))}
          </div>
        </div>
        <div className="cr-corpo">
          <div className="cr-fundo" aria-hidden="true">
            {diasDaRegua.map((d, i) => (d.fds || d.segunda) && (
              <span key={d.iso} className={d.fds ? 'cr-fds' : 'cr-semana'}
                style={{ left: `${(i / dias) * 100}%`, width: d.fds ? `${100 / dias}%` : undefined }} />
            ))}
            {naJanela(hoje) && <span className="cr-hoje" style={linhaDoDia(hoje)} />}
            {naJanela(prazo) && <span className="cr-prazo" style={{ left: `${((emDias(prazo!) + 1 - ini) / dias) * 100}%` }} />}
          </div>
          <ul className="cr-linhas">
            {linhas.map((l) => {
              const atraso = textoDoAtraso(l);
              const atrasado = l.tipo_atraso === 'laboratorio' || l.tipo_atraso === 'amostra';
              const fimReal = l.fim_real ?? (l.inicio_real && !l.concluido ? hoje : null);
              /* O hachurado: do dia seguinte ao previsto (ou da chegada que
                 não veio) até hoje. */
              const desde = l.tipo_atraso === 'laboratorio' && l.fim_previsto
                ? deDias(emDias(l.fim_previsto) + 1)
                : l.tipo_atraso === 'amostra'
                  ? (l.previsao_chegada && l.previsao_chegada < hoje ? l.previsao_chegada : l.inicio_previsto)
                  : null;
              return (
                <li key={l.id} className={`cr-linha${atrasado ? ' atrasada' : ''}`}
                  data-linha={l.id} data-atraso={l.tipo_atraso ?? ''}>
                  <div className="cr-esq">
                    <div className="cr-l1">
                      <span className={`cr-metodo mono escopo-${l.escopo ?? 'outros'}`}
                        title={l.metodo_nome}>{l.metodo_codigo}</span>
                      <AmostraDaLinha l={l} />
                      <SeloDaLinha l={l} />
                    </div>
                    <div className="cr-l2">
                      <span className="cr-datas mono">{textoDasDatas(l, hoje)}</span>
                      {atraso && (
                        <span className={`cr-atraso${atrasado ? ' aberto' : ''}`}>{atraso}</span>
                      )}
                    </div>
                  </div>
                  <div className="cr-trilho" aria-hidden="true">
                    {l.inicio_previsto && l.fim_previsto && (
                      <span className="cr-barra previsto" style={trecho(l.inicio_previsto, l.fim_previsto)} />
                    )}
                    {l.inicio_real && fimReal && (
                      <span className="cr-barra realizado" style={trecho(l.inicio_real, fimReal)} />
                    )}
                    {atrasado && desde && desde <= hoje && (
                      <span className="cr-barra atraso" style={trecho(desde, hoje)} />
                    )}
                    {l.previsao_chegada && naJanela(l.previsao_chegada) && (
                      <span className="cr-chegada" style={linhaDoDia(l.previsao_chegada)} />
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
